/**
 * ELK-based graph layout for the provenance DAG.
 *
 * Takes React Flow nodes/edges and returns them with computed positions.
 * Uses orthogonal edge routing so edges bend around nodes instead of
 * cutting through them.
 */

import ELK, { type ElkNode, type ElkExtendedEdge } from "elkjs/lib/elk.bundled.js";
import type { Node, Edge } from "@xyflow/react";
import {
  ALL_DATA_NODE_WIDTH,
  COMMENT_BADGE_HEIGHT,
  COMMENT_BADGE_WIDTH,
  DETAIL_PILL_HEIGHT,
  DETAIL_PILL_WIDTH,
  PLOT_NODE_HEIGHT,
  PLOT_NODE_WIDTH,
  getAllDataNodeHeight,
  getRefQuantityGroupSize,
  getSourceGroupSize,
  getSweepGroupSize,
} from "./dagNodeSizing";

const elk = new ELK();

export interface ElkPoint {
  x: number;
  y: number;
}

export async function layoutDAG(
  nodes: Node[],
  edges: Edge[],
): Promise<{ nodes: Node[]; edges: Edge[] }> {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const validEdges = edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));

  const graph: ElkNode = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "DOWN",
      "elk.spacing.nodeNode": "60",
      "elk.layered.spacing.nodeNodeBetweenLayers": "100",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.spacing.edgeNode": "25",
      "elk.spacing.edgeEdge": "15",
    },
    children: nodes.map((n) => {
      if (n.type === "detailPill") {
        return { id: n.id, width: DETAIL_PILL_WIDTH, height: DETAIL_PILL_HEIGHT };
      }
      if (n.type === "allDataNode") {
        const runCount = (n.data as { runCount?: number })?.runCount ?? 6;
        return {
          id: n.id,
          width: ALL_DATA_NODE_WIDTH,
          height: getAllDataNodeHeight(runCount),
          layoutOptions: { "elk.layered.layerConstraint": "LAST" },
        };
      }
      if (n.type === "sweepGroup") {
        const d = n.data as { expanded?: boolean; group?: { runs: unknown[] } };
        return {
          id: n.id,
          ...getSweepGroupSize({
            expanded: d.expanded,
            runCount: d.group?.runs.length ?? 0,
          }),
        };
      }
      if (n.type === "refQuantityGroup") {
        const d = n.data as {
          expanded?: boolean;
          labels?: unknown[];
          group?: { surfaceMeshNodes?: unknown[] };
        };
        return {
          id: n.id,
          ...getRefQuantityGroupSize({
            expanded: d.expanded,
            labelCount: d.labels?.length ?? 2,
            hasMeshButton: (d.group?.surfaceMeshNodes?.length ?? 0) > 0,
          }),
        };
      }
      if (n.type === "sourceGroup") {
        const d = n.data as { expanded?: boolean; group?: { nodes: unknown[] } };
        return {
          id: n.id,
          ...getSourceGroupSize({
            expanded: d.expanded,
            nodeCount: d.group?.nodes?.length ?? 0,
          }),
        };
      }
      if (n.type === "plotNode") {
        return { id: n.id, width: PLOT_NODE_WIDTH, height: PLOT_NODE_HEIGHT };
      }
      if (n.type === "commentBadge") {
        return { id: n.id, width: COMMENT_BADGE_WIDTH, height: COMMENT_BADGE_HEIGHT };
      }
      return { id: n.id, width: COMMENT_BADGE_WIDTH, height: COMMENT_BADGE_HEIGHT };
    }),
    edges: validEdges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  const ELK_TIMEOUT_MS = 5000;
  let result;
  try {
    result = await Promise.race([
      elk.layout(graph),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("ELK layout timed out")), ELK_TIMEOUT_MS),
      ),
    ]);
  } catch (e) {
    console.warn("ELK layout failed or timed out, using fallback grid", e);
    return {
      nodes: nodes.map((n, i) => ({
        ...n,
        position: { x: (i % 10) * 200, y: Math.floor(i / 10) * 150 },
      })),
      edges,
    };
  }

  const elkNodeMap = new Map((result.children ?? []).map((c) => [c.id, c]));
  const positionedNodes = nodes.map((node) => {
    const elkNode = elkNodeMap.get(node.id);
    return {
      ...node,
      position: {
        x: elkNode?.x ?? 0,
        y: elkNode?.y ?? 0,
      },
    };
  });

  const elkEdgeMap = new Map<string, ElkExtendedEdge>();
  for (const e of (result.edges ?? []) as ElkExtendedEdge[]) {
    elkEdgeMap.set(e.id, e);
  }

  const positionedEdges = validEdges.map((edge) => {
    const elkEdge = elkEdgeMap.get(edge.id);
    const sections = elkEdge?.sections ?? [];
    const points: ElkPoint[] = [];
    for (const section of sections) {
      points.push(section.startPoint);
      if (section.bendPoints) points.push(...section.bendPoints);
      points.push(section.endPoint);
    }
    if (points.length >= 2) {
      return {
        ...edge,
        type: "elkRouted",
        data: { ...(edge.data ?? {}), elkPoints: points },
      };
    }
    return edge;
  });

  return { nodes: positionedNodes, edges: positionedEdges };
}
